import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useAuthStore } from '@shared/stores/authStore';
import { InvoiceDropdownActions } from '../components/interaction/InvoiceDropdownActions';
import { Invoice } from '../models/Invoices';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const getInvoiceStatus = (invoice: Invoice | undefined): 'creada' | 'borrador' | 'facturada' => {
  if (!invoice) return 'creada';

  const hasErpId = invoice.erp_id !== null && invoice.erp_id !== undefined;
  const hasErpNumber = invoice.erp_number !== null && invoice.erp_number !== undefined;

  // Facturada = Si tiene erp_id y erp_number
  if (hasErpId && hasErpNumber) {
    return 'facturada';
  }

  // Creada = si no tiene erp_id ni erp_number
  if (!hasErpId && !hasErpNumber) {
    return 'creada';
  }

  // Borrador = Si no tiene erp_number (pero tiene erp_id)
  return 'borrador';
};

const convertToTanStackColumns = (agGridColumns: ColDef[], t: (key: string) => string): ColumnDef<any>[] =>
  agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions';
    })
    .map((col) => {
      let headerLabel = col.headerName || 'Columna';

      if (headerLabel?.includes('table.')) {
        headerLabel = t(headerLabel);
      }

      return {
        id: col.field || col.headerName || 'unknown',
        accessorKey: col.field || col.headerName || 'unknown',
        header: headerLabel,
      };
    });

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onViewInvoice?: (invoice: Invoice) => void;
  onDownloadInvoice?: (invoice: Invoice) => void;
  onRegisterContalink?: (invoice: Invoice) => void;
}

export const useAgGridColumns = ({
  selectedColumns = [],
  onViewInvoice,
  onDownloadInvoice,
  onRegisterContalink,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('invoicesModule');
  const currency = useCurrency();
  const team = useAuthStore((state) => state.team);
  const isTenant3pl = team?.type === 'tenant_3pl';
  const hasInvoiceActions = Boolean(onViewInvoice) || Boolean(onDownloadInvoice);

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'erp_number',
        headerName: t('table.erp_number', 'Factura'),
        minWidth: 180,
        sortable: true,
        valueGetter: (params) => params.data?.erp_number ?? '-',
      },
      {
        field: 'date',
        headerName: t('table.date', 'Fecha'),
        minWidth: 140,
        sortable: true,
        valueGetter: (params) => params.data?.date ?? null,
        cellRenderer: (params: any) => <span className="text-sm">{formatDate(params.data?.date)}</span>,
      },
      {
        field: 'type',
        headerName: t('table.type', 'Tipo'),
        minWidth: 120,
        sortable: true,
        valueGetter: (params) => params.data?.type ?? params.data?.shipments?.[0]?.type ?? '-',
        cellRenderer: (params: any) => {
          const type = params.data?.type ?? params.data?.shipments?.[0]?.type ?? '-';
          return <span className="text-sm">{type}</span>;
        },
      },
      {
        field: 'client',
        headerName: t('table.client', 'Cliente'),
        minWidth: 200,
        valueGetter: (params) => params.data?.team?.name ?? params.data?.shipments?.[0]?.client_name ?? '-',
        cellRenderer: (params: any) => {
          const teamName = params.data?.team?.name;
          const fallbackClient = params.data?.shipments?.[0]?.client_name;
          const displayValue = teamName ?? fallbackClient ?? '-';

          return <span className="text-sm">{displayValue}</span>;
        },
      },
      {
        field: 'client_tax_id',
        headerName: t('table.client_tax_id', 'NIF'),
        minWidth: 140,
        valueGetter: (params) => params.data?.team?.vat ?? '-',
        cellRenderer: (params: any) => {
          const vat = params.data?.team?.vat ?? '-';
          return <span className="text-sm">{vat}</span>;
        },
      },
      {
        field: 'grand_total',
        headerName: t('table.grand_total', 'Importe'),
        minWidth: 160,
        sortable: true,
        valueGetter: (params) => params.data?.grand_total ?? 0,
        cellRenderer: (params: any) => {
          const total = params.data?.grand_total ?? 0;
          return <span className="text-sm font-medium">{formatCurrencyAmount(total, currency)}</span>;
        },
      },
    ];

    // Agregar columnas solo para tenant_3pl
    if (isTenant3pl) {
      // Columna de código de prefactura
      baseColumns.push({
        field: 'preinvoice_code',
        headerName: t('table.preinvoice_code', 'Prefactura'),
        minWidth: 140,
        sortable: true,
        valueGetter: (params) => params.data?.preinvoice?.code ?? '-',
        cellRenderer: (params: any) => {
          const preinvoiceCode = params.data?.preinvoice?.code;
          return <span className="text-sm">{preinvoiceCode ?? '-'}</span>;
        },
      });

      // Columna de estado
      baseColumns.push({
        field: 'status',
        headerName: t('table.status', 'Estado'),
        minWidth: 140,
        valueGetter: (params) => {
          const invoice = params.data as Invoice | undefined;
          return getInvoiceStatus(invoice);
        },
        cellRenderer: (params: any) => {
          const invoice = params.data as Invoice | undefined;
          const status = getInvoiceStatus(invoice);

          const statusLabels = {
            creada: t('table.status_created', 'Creada'),
            borrador: t('table.status_draft', 'Borrador'),
            facturada: t('table.status_invoiced', 'Facturada'),
          };

          const statusColors = {
            creada: 'bg-bg-info text-info',
            borrador: 'bg-bg-warning text-warning',
            facturada: 'bg-bg-success text-success',
          };

          return (
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[status]}`}>
              {statusLabels[status]}
            </span>
          );
        },
      });
    }

    if (hasInvoiceActions) {
      baseColumns.push({
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 56,
        maxWidth: 56,
        minWidth: 56,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const invoice: Invoice | undefined = params.data;

          if (!invoice || !invoice.id) {
            return null;
          }

          return (
            <div className="flex justify-center w-full">
              <InvoiceDropdownActions
                invoice={invoice}
                onAction={(action, currentInvoice) => {
                  if (action === 'view') {
                    onViewInvoice?.(currentInvoice);
                    return;
                  }

                  if (action === 'download') {
                    onDownloadInvoice?.(currentInvoice);
                    return;
                  }

                  if (action === 'contalink') {
                    onRegisterContalink?.(currentInvoice);
                  }
                }}
              />
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [currency, t, onDownloadInvoice, onViewInvoice, onRegisterContalink, hasInvoiceActions, isTenant3pl]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';

      if (field === 'actions') {
        return hasInvoiceActions;
      }

      return selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns, hasInvoiceActions]);

  return {
    filteredColumnDefs,
    tanStackColumns,
  };
};
